import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { AssignTellerManagementWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AlertModalBinIcon } from "../../../assets";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { AssignTellerTable } from "../../../containers/tables/assigntellertable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllTellerService } from "../../../utils/api/usermanagement/assignteller/retrieveAllTellers";
import { deleteTellerService } from "../../../utils/api/usermanagement/assignteller/deleteTeller";

export const AssignTellerManagement = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [tellerIdToDelete, setTellerIdToDelete] = useState<string | null>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("User Management");
	}, [setActiveNavItem]);

	const { data: tellers, refetch: refetchAllTeller } = useQuery({
		queryKey: ["allteller", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllTellerService(TOKEN, paginationIndex);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleAssignTellerButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/usermanagement/assignteller");
	};

	const handleActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string | Record<any, any>,
		action?: string,
	) => {
		e.preventDefault();
		switch (action) {
			case "edit":
				return navigate(`/usermanagement/teller/${id}`);
			case "delete":
				if (typeof id !== "string") {
					const { tellerId, institutionId } = id;
					return handleDeleteTeller(e, tellerId, institutionId);
				}
				break;
			default:
				console.warn(`Unhandled action: ${action}`);
		}
	};

	const handleDeleteTeller = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		tellerId: string,
		institutionId: string,
	) => {
		e.preventDefault();
		setError(null);
		setTellerIdToDelete(tellerId);
		try {
			const response = await deleteTellerService(
				TOKEN,
				tellerId,
				institutionId,
			);
			if (response.status === "success") {
				setTellerIdToDelete(null);
				setIsAlertModalOpen(true);
				refetchAllTeller();
			} else {
				setTellerIdToDelete(null);
				setError("Teller deletion failed. Please try again.");
			}
		} catch (error: any) {
			setTellerIdToDelete(null);
			setError(`Teller deletion failed. ${error.message}`);
			console.error("Teller deletion failed:", error);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout pageId={"Assign-Teller-Management"} pageTitle={"Assign Teller"}>
			<AssignTellerManagementWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalBinIcon />}
					title="Teller Successfully Deleted"
					callToAction="Close"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleAssignTellerButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Assign Teller
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="assign-teller-management-table-box">
					<AssignTellerTable
						rows={tellers ?? []}
						selectedTellerId={tellerIdToDelete}
						handleItemClick={handleActionClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				{error && (
					<Typography
						fontFamily={"Inter"}
						fontWeight={"600"}
						fontSize={14}
						lineHeight={"normal"}
						color={"var(--text-salmon-pink-color)"}
						whiteSpace={"normal"}
					>
						{error}
					</Typography>
				)}
			</AssignTellerManagementWrapper>
		</AppLayout>
	);
};
